import React, { useEffect, useState } from "react";
import { Gauge, AlertTriangle } from "lucide-react";
import { PageHeader } from "@/components/Layout";
import { StatusBadge, PriorityBadge, EmptyState } from "@/components/Bits";
import { roleLabel } from "@/lib/ui";
import api from "@/lib/api";

function isOverdue(due) {
  if (!due) return false;
  return new Date(due) < new Date(new Date().toDateString());
}

export default function Workload() {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/team/workload").then((r) => setRows(r.data));
  }, []);

  const maxOpen = Math.max(1, ...rows.map((r) => r.open_count));

  return (
    <div>
      <PageHeader title="Team Workload" subtitle="Open tasks and due dates across the team, at a glance" />
      <div className="p-8">
        {rows.length === 0 ? (
          <EmptyState icon={Gauge} title="No team data" hint="Add team members and assign tasks." />
        ) : (
          <div className="space-y-4">
            {rows.map((m) => (
              <div key={m.id} data-testid={`workload-${m.id}`} className="bg-[#121212] border border-white/10 rounded-sm p-5">
                <div className="flex items-center gap-4">
                  {m.avatar ? (
                    <img src={m.avatar} alt={m.name} className="w-11 h-11 rounded-sm object-cover border border-white/10" />
                  ) : (
                    <div className="w-11 h-11 rounded-sm bg-white/10 border border-white/10 flex items-center justify-center font-display font-bold">{m.name.charAt(0)}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-display text-base font-medium">{m.name}</h3>
                        <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{roleLabel(m.role)}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-display text-2xl font-bold">{m.open_count}</span>
                        <span className="text-xs text-zinc-500 ml-1">open</span>
                      </div>
                    </div>
                    <div className="mt-2 h-1.5 bg-white/10 rounded-sm overflow-hidden">
                      <div className="h-full bg-white transition-all duration-500" style={{ width: `${(m.open_count / maxOpen) * 100}%` }} />
                    </div>
                  </div>
                </div>

                {m.tasks.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-1 md:grid-cols-2 gap-2">
                    {m.tasks.map((t) => (
                      <div key={t.id} className="flex items-center justify-between bg-[#0a0a0a] border border-white/10 rounded-sm px-3 py-2">
                        <div className="min-w-0">
                          <div className="text-sm text-zinc-200 truncate">{t.title}</div>
                          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 flex items-center gap-2">
                            <span>{t.brand}</span>
                            {t.due_date && (
                              <span className={`flex items-center gap-1 ${isOverdue(t.due_date) ? "text-[#FF6259]" : "text-zinc-500"}`}>
                                {isOverdue(t.due_date) && <AlertTriangle size={11} />} {t.due_date}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <PriorityBadge priority={t.priority} />
                          <StatusBadge status={t.status} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
